import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Camera, Upload, ScanFace } from "lucide-react";
import { useHOH } from "../../../context/StyleContext";
import StepFrame, { StepTitle } from "../StepFrame";
import ScanVFX from "./ScanVFX";
import { toast } from "sonner";

const SCAN_STATES = [
  "Reading face shape",
  "Detecting style direction",
  "Understanding hair texture",
  "Preparing hairstyle previews",
];

export default function PhotoUploadStep() {
  const { photo, setPhoto, scanning, runScan } = useHOH();
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!scanning) return;
    setIdx(0);
    const t = setInterval(() => setIdx((p) => Math.min(p + 1, SCAN_STATES.length - 1)), 700);
    return () => clearInterval(t);
  }, [scanning]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image"); return; }
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <StepFrame className="text-center">
      <StepTitle overline="Step 2" title="Upload your photo" subtitle="Your photo is used to preview hairstyles and your final look." />

      <div className="flex flex-col items-center">
        <div className="relative w-60 h-60 md:w-72 md:h-72">
          <div className={`w-full h-full rounded-full overflow-hidden border-4 border-white shadow-xl bg-hoh-lavender flex items-center justify-center ${scanning ? "hoh-glow-border" : ""}`}>
            {photo ? <img src={photo} alt="Your selfie" className="w-full h-full object-cover" data-testid="photo-preview" /> : <Camera className="w-14 h-14 text-hoh-rose/50" />}
            {scanning && <ScanVFX />}
          </div>
        </div>

        {scanning ? (
          <motion.p key={idx} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-8 text-lg font-heading font-semibold text-black" data-testid="scan-state">
            {SCAN_STATES[idx]}…
          </motion.p>
        ) : (
          <>
            <input ref={galleryRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="gallery-input" />
            <input ref={cameraRef} type="file" accept="image/*" capture="user" hidden onChange={onFile} data-testid="camera-input" />
            <div className="mt-8 flex gap-3">
              <button onClick={() => cameraRef.current?.click()} className="btn-ghost !px-6 !py-3 !text-xs" data-testid="take-photo-btn">
                <Camera className="w-4 h-4" /> Take Photo
              </button>
              <button onClick={() => galleryRef.current?.click()} className="btn-ghost !px-6 !py-3 !text-xs" data-testid="upload-gallery-btn">
                <Upload className="w-4 h-4" /> Upload
              </button>
            </div>
            <button onClick={runScan} disabled={!photo} className="btn-royal mt-6" data-testid="scan-btn">
              <ScanFace className="w-4 h-4" /> Scan
            </button>
          </>
        )}
      </div>
    </StepFrame>
  );
}
